import { defineStore } from 'pinia';
import { ref } from 'vue';
import { reserveCart } from '../../../utils/API/Cart/cartAPI';
import { Log } from '../../../utils/API/Logging/LoggingAPI';
import { useCustomerInformationStore } from '../../checkout/stores/customerInformationStore';
import { useClickAndCollectLocationStore } from './clickAndCollectLocationStore';

const GENERIC_ERROR_MESSAGE = 'Kunne ikke fullføre reservasjonen. Prøv igjen.';

/**
 * Submits the click & collect reservation (`POST /api/cart/reserve`) using the
 * click & collect form fields and the selected warehouse, and tracks the
 * submission's state. No polling/retry logic lives here - retry-on-not-found
 * for the resulting order is a backend concern (see `IClickCollectOrderService`).
 */
export const useClickAndCollectReservationStore = defineStore('clickAndCollectReservation', () => {
  // State
  const isSubmitting = ref(false);
  const isSuccess = ref(false);
  const errorMessage = ref('');
  const orderNumber = ref('');

  // Actions
  async function reserveOrder(): Promise<boolean> {
    const customerInformationStore = useCustomerInformationStore();
    const clickAndCollectLocationStore = useClickAndCollectLocationStore();
    const form = customerInformationStore.fields;

    isSubmitting.value = true;
    isSuccess.value = false;
    errorMessage.value = '';

    try {
      const response = await reserveCart({
        warehouseCode: clickAndCollectLocationStore.warehouseCode ?? '',
        customerName: `${form.firstName} ${form.lastName}`.trim(),
        customerEmail: form.email ?? '',
        customerPhone: form.phoneNumber ?? '',
      });

      if (!response.createdOk) {
        errorMessage.value = response.errorMessage || GENERIC_ERROR_MESSAGE;
        isSuccess.value = false;
        return false;
      }

      orderNumber.value = response.orderNumber ?? '';
      isSuccess.value = true;
      return true;
    } catch (error) {
      Log({
        LogLevel: 'Error',
        Message: `[clickAndCollectReservationStore] Failed to reserve order: ${error instanceof Error ? error.message : String(error)}`,
      });
      errorMessage.value = GENERIC_ERROR_MESSAGE;
      isSuccess.value = false;
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  function reset() {
    isSubmitting.value = false;
    isSuccess.value = false;
    errorMessage.value = '';
    orderNumber.value = '';
  }

  return {
    // State
    isSubmitting,
    isSuccess,
    errorMessage,
    orderNumber,
    // Actions
    reserveOrder,
    reset,
  };
});
